/**
 * <lazy-section data-url="..."> fetches server-rendered HTML when it nears the viewport and swaps itself for it.
 * Used to keep below-the-fold sections out of the initial HTML (less Liquid render time and smaller documents).
 * The fetched URL should point to a template with "layout": false so only the section markup is returned.
 */
if (!customElements.get('lazy-section')) {
  class LazySection extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url || this.loading) return;

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        },
        { rootMargin: this.dataset.rootMargin || '800px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer && this.observer.disconnect();
    }

    async load() {
      this.loading = true;
      try {
        const response = await fetch(this.dataset.url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        this.render(html);
      } catch (error) {
        console.warn('[lazy-section] failed to load', this.dataset.url, error);
        this.remove();
      }
    }

    render(html) {
      let fragment = document.createRange().createContextualFragment(html);
      const parent = this.parentNode;

      // Section Rendering API responses are wrapped in the section's own <div id="shopify-section-…">, which
      // already exists around this placeholder; keep only its contents to avoid a nested duplicate id.
      if (this.hasAttribute('data-unwrap-section')) {
        const wrapper = fragment.querySelector('.shopify-section');
        if (wrapper) {
          const inner = document.createDocumentFragment();
          while (wrapper.firstChild) inner.appendChild(wrapper.firstChild);
          fragment = inner;
        }
      }

      // Scripts in a contextual fragment run when inserted. Drop external scripts that are already on the
      // page so custom elements (e.g. product-slider.js) are not defined twice.
      fragment.querySelectorAll('script[src]').forEach((script) => {
        if (document.querySelector(`script[src="${script.getAttribute('src')}"]`)) script.remove();
      });

      this.replaceWith(fragment);

      // Let integrations that already re-bind after AJAX-rendered product cards pick up the new ones.
      try {
        document.dispatchEvent(new CustomEvent('Flits:AjaxCollection:CollectionUpdated'));
        document.dispatchEvent(new CustomEvent('clickpost-edd:cards-added'));
        document.dispatchEvent(new CustomEvent('lazy-section:loaded', { detail: { parent } }));
      } catch (e) {}
    }
  }

  customElements.define('lazy-section', LazySection);
}
